import * as React from 'react';
import Button from 'antd/lib/button';
import {
    WorkflowRunPhasedReleaseWidgetsContainer,
    WorkflowRunPhasedReleaseWidgetsContainerProps,
} from './WorkflowRunPhasedReleaseWidget';
import Flex from 'antd/lib/flex';
import { Link } from 'react-router-dom';
import RightArrowCircledIcon from '@/components/icons/RightArrowCircledIcon';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectUserIsRunwayAdmin } from '@/selectors/selectors';
import { trackAppRouteEvent } from '@/actions/trackEvent';
import * as Analytics from '../../../utils/analytics';
import { ReleaseStepType } from '@/types';
import { ArchetypeGroupSummaryDetails } from '@/types/workflowOverview';
import { createStepRunRoute, createStepRunsSummaryRoute, shouldLinkToStepRunsSummary } from '@/helpers/routeHelpers';
import { WorkflowRunStepRunGraph } from '@/types/workflowRunStepRunGraph';

export interface WorkflowRunRolloutWidgetsContainerProps extends WorkflowRunPhasedReleaseWidgetsContainerProps {
    orgIdParam: string;
    appIdParam: string;
    releaseIdParam: string;
    workflowEnabledSteps: Partial<Record<ReleaseStepType, boolean>>;
    archetypeGroupDetailsMap: Record<string, ArchetypeGroupSummaryDetails>;
    runGraph?: WorkflowRunStepRunGraph;
}

export const WorkflowRunRolloutWidgetsContainer: React.FC<WorkflowRunRolloutWidgetsContainerProps> = ({
    appId,
    orgIdParam,
    appIdParam,
    releaseIdParam,
    phasedGroups,
    appStoreBuildsByDestStepConfigId,
    stabilityIntegrations,
    rolloutMonitoringByDestStepConfigId,
    allDestinations,
    selectedDestinationStepConfigIds,
    userSelectedMultipleAPKBuildId,
    workflowEnabledSteps,
    archetypeGroupDetailsMap,
    runGraph,
}) => {
    const dispatch = useDispatch();
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));

    const trackViewRolloutMonitoring = () => {
        dispatch(
            trackAppRouteEvent({
                orgId: orgIdParam,
                appId: appIdParam,
                eventName: Analytics.Event.ViewRolloutMonitoring,
            }),
        );
    };

    const haveAnyPhasedAPIs = !!phasedGroups && phasedGroups?.length > 0;

    // TODO-WF-OVERVIEW / TODO-WF-ROLLOUT: add a flag haveAnyMonitoringStats that captures whether we have any APIs for
    // monitoring statistics; then evaluate haveAnyRolloutsData as haveAnyPhasedAPIs || haveAnyMonitoringStats. As below
    // with haveAnyPhasedAPIs / phased widgets, render monitoring widgets conditionally on the value of haveAnyMonitoringStats.
    const haveAnyRolloutsData = haveAnyPhasedAPIs;

    if (!haveAnyRolloutsData) {
        return null;
    }

    const isRolloutsStepMigratedAndEnabled = !!workflowEnabledSteps[ReleaseStepType.Rollout];

    const rolloutsRoute = React.useMemo(() => {
        const targetArchetype = isRolloutsStepMigratedAndEnabled ? ReleaseStepType.Rollout : ReleaseStepType.Takeoff;
        const archGroup = Object.values(archetypeGroupDetailsMap ?? {}).find(
            (details) => details.archetype === targetArchetype,
        );
        const stepRunIds = archGroup?.archetypeGroupStepRunIds ?? [];
        if (stepRunIds.length === 0) {
            return '';
        }

        if (
            isRolloutsStepMigratedAndEnabled &&
            shouldLinkToStepRunsSummary(ReleaseStepType.Rollout, stepRunIds.length, isRunwayAdmin)
        ) {
            const archetypeGroupIndex =
                runGraph?.stepGroups?.[archGroup.parentStepGroupIndex]?.stepArchetypeGroups?.findIndex(
                    (archetypeGroup) => archetypeGroup.id === archGroup.archetypeGroupId,
                ) ?? -1;
            if (archetypeGroupIndex >= 0) {
                return createStepRunsSummaryRoute(
                    orgIdParam,
                    appIdParam,
                    releaseIdParam,
                    archGroup.parentStepGroupIndex,
                    archetypeGroupIndex,
                );
            }
        }

        return createStepRunRoute(orgIdParam, appIdParam, releaseIdParam, stepRunIds[0]);
    }, [
        archetypeGroupDetailsMap,
        isRolloutsStepMigratedAndEnabled,
        isRunwayAdmin,
        runGraph,
        orgIdParam,
        appIdParam,
        releaseIdParam,
    ]);

    const gotValidRolloutsRoute = !!rolloutsRoute;

    return (
        <Flex
            vertical
            gap={10}
            align={'stretch'}
            style={{
                marginTop: 20,
                marginBottom: 20,
            }}
        >
            {haveAnyPhasedAPIs && (
                <WorkflowRunPhasedReleaseWidgetsContainer
                    appId={appId}
                    phasedGroups={phasedGroups}
                    appStoreBuildsByDestStepConfigId={appStoreBuildsByDestStepConfigId}
                    rolloutMonitoringByDestStepConfigId={rolloutMonitoringByDestStepConfigId}
                    allDestinations={allDestinations}
                    selectedDestinationStepConfigIds={selectedDestinationStepConfigIds}
                    stabilityIntegrations={stabilityIntegrations}
                    userSelectedMultipleAPKBuildId={userSelectedMultipleAPKBuildId}
                />
            )}
            {gotValidRolloutsRoute && (
                <Link to={rolloutsRoute} style={{ alignSelf: 'flex-end' }}>
                    <Button
                        type="link"
                        style={{
                            fontSize: 13,
                            fontWeight: 200,
                            paddingRight: 0,
                        }}
                        onClick={() => trackViewRolloutMonitoring()}
                    >
                        <div
                            style={{
                                display: 'inline-flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 8,
                                height: '100%',
                            }}
                        >
                            Rollout
                            <RightArrowCircledIcon />
                        </div>
                    </Button>
                </Link>
            )}
        </Flex>
    );
};
